import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {CopiedCellOffsetsKey, CopyPasteState} from '@mintdata-wsp/common/state/ui/visualDesigner/CopyPasteState';
import {ILinearFormulaToken} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {MeshCellNote} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellNote';
import {MeshCellSettings} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshCopiedCell} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCopiedCell';
import {MeshLocation, MeshLocationKey} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshSheetSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshSheetSize';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {SlotDependencies, SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotGlobalKey, SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {SpreadsheetsState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SpreadsheetsState';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {SpreadsheetViewState} from '@mintdata-wsp/common/state/ui/visualDesigner/SpreadsheetViewState';
import {AbstractSheetCopyPasteController} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/AbstractSheetCopyPasteController';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {ISheetLocation} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {deserialize, serialize} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import {StringUtil} from '@mintdata/mint-kit/impl/util/StringUtil';
import bind from 'bind-decorator';
import {action, IReactionDisposer, reaction} from 'mobx';

export enum ClipboardKey
{
    PlainText = 'text/plain',
    Html = 'text/html',
    CopyPasteState = 'application/x-mint-copy-paste-state',
    CloudCopyPasteState = 'application/x-mint-cloud-copy-paste-state',
}

// todo: This MeshCopyPasteController works only with client sheets. There should be a separate controller for cloud sheets.
export class MeshCopyPasteController extends AbstractSheetCopyPasteController
{
    public static PasteSplitRegexp = /\t/;

    private _copiedRangeVisibilityDisposer:IReactionDisposer = null;

    private _addCopiedRangeVisibilityReaction(sheetId):void
    {
        this._copiedRangeVisibilityDisposer = reaction(
            () =>
            {
                const openDesign = commonControllers.designState.getOpenDesign();
                return openDesign && SpreadsheetsState.toJson(openDesign.designState.spreadsheets);
            },
            () =>
            {
                this._hideCopiedRange(sheetId);

                if( this._copiedRangeVisibilityDisposer )
                {
                    this._copiedRangeVisibilityDisposer();
                    this._copiedRangeVisibilityDisposer = null;
                }
            }
        );
    }

    @action.bound
    public getCopyPasteState(sheetId:SheetId):CopyPasteState | null
    {
        const viewState = this._getSpreadsheetViewState(sheetId);
        if( !viewState )
            return null;

        return viewState.copyPasteState;
    }

    @bind
    protected _getSpreadsheetViewState(sheetId:SheetId, includeFrameDetailsSheets:boolean = false):SpreadsheetViewState | null
    {
        const main = controllers.ui.spreadsheets.main;

        const sheet = main.findAnySheetById(sheetId, includeFrameDetailsSheets) as Sheet;
        if( !sheet )
            return null;

        const workbook:Workbook = main.findWorkbookById(sheet.workbook.id);
        Assert.yes(workbook, `Unknown workbook: ${sheet.workbook.id} for sheet: ${sheetId}`);

        return workbook.view;
    }

    @action.bound
    public copySlotToClipboard(sheetId:SheetId,
                               isCut:boolean = false,
                               isNestedSheet:boolean = false):void
    {
        const spreadsheetState = this._getSpreadsheetViewState(sheetId, isNestedSheet);

        if( !spreadsheetState )
            return;

        const ctx:IWSPMeshContext = this._getMeshContextBySheetId(sheetId, isNestedSheet);
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

        if( meshState.details.isReadonly )
            isCut = false;

        if( meshState.details.selectedRange )
        {
            let from = meshState.details.selectedRange.from;
            let to = meshState.details.selectedRange.to;

            if( from.colIndex === null )
            {
                from = new MeshLocation(0, from.rowIndex);
                to = new MeshLocation(meshState.size.numCols - 1, to.rowIndex);
            }

            if( from.rowIndex === null )
            {
                from = new MeshLocation(from.colIndex, 0);
                to = new MeshLocation(to.colIndex, meshState.size.numRows - 1);
            }

            spreadsheetState.setCopyPasteState(new CopyPasteState(
                sheetId,
                new MeshRange(from, to || from),
                new Map<CopiedCellOffsetsKey, MeshCopiedCell>(),
                isCut
            ));
            this._addCopiedRangeVisibilityReaction(sheetId);

            const rows:Array<string> = [];

            for( let rowIndex = from.rowIndex; rowIndex <= (to || from).rowIndex; rowIndex++ )
            {
                const rowValues:Array<string> = [];

                for( let colIndex = from.colIndex; colIndex <= (to || from).colIndex; colIndex++ )
                {
                    const location = new SlotLocation(colIndex, rowIndex);
                    const meshLocation = MeshLocation.fromSlotLocation(location);

                    const slot = controllers.ui.spreadsheets.sheet.findSlot(sheetId, location);

                    rowValues.push(slot ? slot.valueAsString : '');

                    const colOffset:number = colIndex - from.colIndex;
                    const rowOffset:number = rowIndex - from.rowIndex;

                    let {value, formula, isLazy} = slot || {} as Slot;

                    const appliedSheetSettings = controllers.ui.spreadsheets.sheetSettings.getAppliedSheetSettings(sheetId, meshLocation);
                    const appliedFormat = controllers.ui.spreadsheets.sheetSettings.getCellFormat(sheetId, meshLocation.asKey()).id;
                    let namedRangeIds = [];

                    if( isCut )
                    {
                        namedRangeIds = controllers.ui.spreadsheets.namedRanges
                            .getNamedRangesByLocation(sheetId, location)
                            .map(range => range.id);
                    }

                    const copiedCell = new MeshCopiedCell(
                        colOffset,
                        rowOffset,
                        value,
                        formula,
                        location,
                        appliedSheetSettings,
                        appliedFormat,
                        namedRangeIds,
                        isLazy
                    );

                    const key = CopyPasteState.makeCopiedCellOffsetsKey(colOffset, rowOffset);
                    spreadsheetState.copyPasteState.copiedRangeCells.set(key, copiedCell);
                }

                rows.push(rowValues.join(','));
            }

            const snapshot = serialize(spreadsheetState.copyPasteState);

            const plainText:string = rows.join('\n');
            const copyPasteJson = JSON.stringify(snapshot);

            this._copyToClipboard(plainText, copyPasteJson);
        }
    }

    private _copyToClipboard(plainText:string, copyPasteState:string):void
    {
        const copyHandler = (event:any) =>
        {
            event.preventDefault();
            event.stopPropagation();

            if( event.clipboardData && event.clipboardData.setData )
            {
                event.clipboardData.setData(ClipboardKey.PlainText, plainText);
                event.clipboardData.setData(ClipboardKey.CopyPasteState, copyPasteState);
            }

            document.removeEventListener('copy', copyHandler, true);
        };

        document.addEventListener('copy', copyHandler, true);
        document.execCommand('copy');
    }

    @action.bound
    public pasteSlotFromClipboard(sheetId:SheetId,
                                  event:React.ClipboardEvent<HTMLDivElement>,
                                  isNestedSheet:boolean = false):void
    {
        event.preventDefault();
        event.stopPropagation();

        const clipboardData = event.clipboardData;
        if( clipboardData && clipboardData.getData )
        {
            const ctx:IWSPMeshContext = this._getMeshContextBySheetId(sheetId, isNestedSheet);
            const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
            if( meshState.details.isReadonly )
            {
                mk.warn(area.Slot, `sheet is readonly: ${sheetId}`);
                return;
            }

            if( meshState.details.selectedRange )
            {
                const plainText = clipboardData.getData(ClipboardKey.PlainText);

                const isCopyPasteState = clipboardData.types.indexOf(ClipboardKey.CopyPasteState) !== -1;
                if( isCopyPasteState )
                {
                    const copyPasteJsonString = clipboardData.getData(ClipboardKey.CopyPasteState);
                    const copyPasteJson = JSON.parse(copyPasteJsonString);
                    const copyPasteState = deserialize(CopyPasteState, copyPasteJson) as CopyPasteState;

                    this._pasteSlotsFromState(sheetId, copyPasteState);
                }
                else
                    this._pasteText(sheetId, plainText);
            }
        }
    }

    @action.bound
    public pasteValuesOnly(sheetId):void
    {
        const copyPasteState:CopyPasteState = this.getCopyPasteState(sheetId);

        this._pasteSlotsFromState(sheetId, copyPasteState, true);
    }

    @bind
    public hasCopiedData(sheetId:SheetId):boolean
    {
        const copyPasteState = this.getCopyPasteState(sheetId);

        return copyPasteState && copyPasteState.copiedSheetId === sheetId;
    }

    @action.bound
    public resetCopiedData(sheetId:SheetId):void
    {
        const spreadsheetState = this._getSpreadsheetViewState(sheetId);

        if( spreadsheetState )
            spreadsheetState.setCopyPasteState(null);
    }

    @action.bound
    private _hideCopiedRange(sheetId:SheetId):void
    {
        const copyPasteState = this.getCopyPasteState(sheetId);

        if( copyPasteState )
            copyPasteState.isCopiedRangeVisible = false;
    }

    @bind
    public isCopiedRangeVisible(sheetId:SheetId):boolean
    {
        const spreadsheetState = this._getSpreadsheetViewState(sheetId);
        return spreadsheetState &&
            spreadsheetState.copyPasteState &&
            spreadsheetState.copyPasteState.copiedSheetId === sheetId &&
            spreadsheetState.copyPasteState.isCopiedRangeVisible;
    }

    private _pasteSlotsFromState(sheetId:SheetId, copyPasteState:CopyPasteState, pasteValuesOnly:boolean = false)
    {
        const {mesh} = controllers.ui.spreadsheets;

        const ctx:IWSPMeshContext = this._getMeshContextBySheetId(sheetId);
        const meshState = mesh.getState(ctx);

        // -----------------------------------------------------------------
        let {details: {selectedRange: {from, to}}} = meshState;

        if( from.colIndex === null )
        {
            from = new MeshLocation(0, from.rowIndex);
            to = new MeshLocation(meshState.size.numCols - 1, to.rowIndex);
        }

        if( from.rowIndex === null )
        {
            from = new MeshLocation(from.colIndex, 0);
            to = new MeshLocation(to.colIndex, meshState.size.numRows - 1);
        }

        const {columnsCount, rowsCount} = new MeshRange(from, to || from);
        // -----------------------------------------------------------------

        if( copyPasteState )
        {
            const {
                copiedSheetId,
                copiedRange,
                copiedRangeCells,
                copiedIsCut
            } = copyPasteState;

            const copyPasteStateLocal:CopyPasteState = this.getCopyPasteState(sheetId);
            const isCopyPasteStateLocal:boolean = copyPasteStateLocal && copyPasteState.id === copyPasteStateLocal.id;

            const copiedCtx:IWSPMeshContext = this._getMeshContextBySheetId(copiedSheetId);
            const copiedMeshState:MeshState = copiedCtx && mesh.getState(copiedCtx);

            // will execute this code block if data pastes from current design
            if( copiedIsCut && !pasteValuesOnly && isCopyPasteStateLocal )
            {
                if( !copyPasteStateLocal.isStale )
                {
                    const slotsGlobalKeysWithRefsToCutCells:Set<SlotGlobalKey> = this._getSlotsGlobalKeysWithRefsToCutCells(
                        copiedSheetId,
                        sheetId,
                        from.colIndex,
                        from.rowIndex,
                        copiedRange
                    );
                    this._pasteCopiedCells(copiedSheetId, sheetId, from.colIndex, from.rowIndex, copiedRangeCells, copiedRange, true);

                    if( copiedMeshState )
                    {
                        this._recalculateCellRefsForSlotsWithRefsToCutCells(
                            copiedSheetId,
                            sheetId,
                            from.colIndex,
                            from.rowIndex,
                            copiedRange,
                            slotsGlobalKeysWithRefsToCutCells
                        );
                        this._relocateNamedRanges(copiedSheetId, sheetId, from.colIndex, from.rowIndex, copiedRangeCells);
                        this._clearCutCells(copiedSheetId, sheetId, from.colIndex, from.rowIndex, copiedRangeCells);

                        copyPasteStateLocal.isStale = true;
                    }

                    this.adjustSelectedRange(meshState, from.colIndex, from.rowIndex, copiedRange.columnsCount, copiedRange.rowsCount);
                }
            }
            else
            {
                const colsRepeatCount = Math.max(1, Math.floor(columnsCount / copiedRange.columnsCount));
                const rowsRepeatCount = Math.max(1, Math.floor(rowsCount / copiedRange.rowsCount));

                for( let colRepeatIndex = 0; colRepeatIndex < colsRepeatCount; colRepeatIndex++ )
                {
                    for( let rowRepeatIndex = 0; rowRepeatIndex < rowsRepeatCount; rowRepeatIndex++ )
                    {
                        this._pasteCopiedCells(
                            copiedSheetId,
                            sheetId,
                            from.colIndex + colRepeatIndex * copiedRange.columnsCount,
                            from.rowIndex + rowRepeatIndex * copiedRange.rowsCount,
                            copiedRangeCells,
                            copiedRange,
                            false,
                            pasteValuesOnly
                        );
                    }
                }
                this.adjustSelectedRange(
                    meshState,
                    from.colIndex,
                    from.rowIndex,
                    colsRepeatCount * copiedRange.columnsCount,
                    rowsRepeatCount * copiedRange.rowsCount
                );
            }

            setTimeout(action(() =>
            {
                if( copiedSheetId && copiedRange && copiedMeshState )
                    controllers.ui.spreadsheets.mesh.calcRowHeight(copiedCtx, copiedRange);

                mesh.calcRowHeight(ctx);
            }), 0);
        }
    }

    private _pasteText(sheetId:SheetId, plainText:string)
    {
        const ctx:IWSPMeshContext = this._getMeshContextBySheetId(sheetId);
        const {mesh, sheet} = controllers.ui.spreadsheets;

        const meshState = mesh.getState(ctx);
        const from = meshState.details.selectedRange.from;

        let maxColumnsCount = 1;

        plainText = StringUtil.replaceUnicodeQuotesIntoASCII(plainText);
        const lines = plainText.split(/\r?\n/);

        const linesLength = lines.length;
        const columnsLength = linesLength ? lines[0].split(MeshCopyPasteController.PasteSplitRegexp).length : 0;

        if( linesLength * columnsLength > 100 )
        {
            this._pasteIntoFrame(sheetId, from, lines);
            return;
        }

        // paste on topLevel

        const numberOfRowsToAddToSheet = lines.length - (meshState.size.numRows - from.rowIndex);

        if( numberOfRowsToAddToSheet > 0 )
        {
            sheet.insertRows(
                sheetId,
                meshState.size.numRows,
                numberOfRowsToAddToSheet
            );
        }

        for( let lineIndex = 0; lineIndex < lines.length; lineIndex++ )
        {
            const lineText = lines[lineIndex];
            const columns = lineText.split(MeshCopyPasteController.PasteSplitRegexp);

            const numberOfColsToAddToSheet = columns.length - (meshState.size.numCols - from.colIndex);

            if( numberOfColsToAddToSheet > 0 )
            {
                sheet.insertColumns(
                    sheetId,
                    meshState.size.numCols,
                    numberOfColsToAddToSheet
                );
            }

            maxColumnsCount = Math.max(maxColumnsCount, columns.length);

            for( let columnIndex = 0; columnIndex < columns.length; columnIndex++ )
            {
                const columnText = columns[columnIndex];

                sheet.setSlotFormula(
                    sheetId,
                    new SlotLocation(from.colIndex + columnIndex, from.rowIndex + lineIndex),
                    columnText
                );
            }
        }

        this.adjustSelectedRange(meshState, from.colIndex, from.rowIndex, maxColumnsCount, lines.length);

        setTimeout(action(() => mesh.calcRowHeight(ctx)), 0);
    }

    private _pasteIntoFrame(sheetId:SheetId, to:MeshLocation, rows:Array<string>)
    {
        const {sheet} = controllers.ui.spreadsheets;

        const fields = [];
        const data = [];

        for( let rowIndex = 0; rowIndex < rows.length; rowIndex++ )
        {
            const rowText = rows[rowIndex];
            const columns = rowText.split(MeshCopyPasteController.PasteSplitRegexp);

            data.push([]);
            
            if( fields.length < columns.length )
            {
                for( let i = fields.length; i < columns.length; i++)
                {
                    fields.push(new FrameField(`column${i + 1}`, Type.String));
                }
            }

            for( let columnIndex = 0; columnIndex < columns.length || columnIndex < fields.length; columnIndex++ )
            {
                if( columnIndex >= columns.length )
                {
                    data[rowIndex].push(AEmpty.Value);
                }
                else
                {
                    const columnText:string = columns[columnIndex];
                    data[rowIndex].push(columnText ? AString.fromString(columnText) : AEmpty.Value);
                }
            }
        }

        data.forEach((row: any[]) => {
            while( row.length < fields.length )
            {
                row.push(AEmpty.Value);
            }
        })

        const frame = Frames.fromFieldsAndData(fields, data);

        sheet.setSlotFormula(
            sheetId,
            new SlotLocation(to.colIndex, to.rowIndex),
            frame
        );
    }

    private _pasteCopiedCells(srcSheetId:SheetId,
                              destSheetId:SheetId,
                              toColIndex:number,
                              toRowIndex:number,
                              copiedRangeCells:Map<CopiedCellOffsetsKey, MeshCopiedCell>,
                              copiedRange:MeshRange,
                              needRecalculateCellRefs:boolean = false,
                              pasteValuesOnly:boolean = false):void
    {
        const ctx:IWSPMeshContext = this._getMeshContextBySheetId(destSheetId);
        const {mesh, formulaInput: fic, sheet, sheetSettings} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const colsFromCopiedRangeCount = copiedRange.columnsCount;
        const rowsFromCopiedRangeCount = copiedRange.rowsCount;
        const numberOfColsToAddToSheet = colsFromCopiedRangeCount - (meshState.size.numCols - toColIndex);
        const numberOfRowsToAddToSheet = rowsFromCopiedRangeCount - (meshState.size.numRows - toRowIndex);

        if( numberOfColsToAddToSheet > 0 )
        {
            sheet.insertColumns(
                destSheetId,
                meshState.size.numCols,
                numberOfColsToAddToSheet
            );
        }

        if( numberOfRowsToAddToSheet > 0 )
        {
            sheet.insertRows(
                destSheetId,
                meshState.size.numRows,
                numberOfRowsToAddToSheet
            );
        }

        for( let colOffset = 0; colOffset < colsFromCopiedRangeCount; colOffset++ )
        {
            for( let rowOffset = 0; rowOffset < rowsFromCopiedRangeCount; rowOffset++ )
            {
                const destColIndex = toColIndex + colOffset;
                const destRowIndex = toRowIndex + rowOffset;
                const destLocation = new MeshLocation(destColIndex, destRowIndex);
                const key = CopyPasteState.makeCopiedCellOffsetsKey(colOffset, rowOffset);

                if( pasteValuesOnly )
                {
                    if( copiedRangeCells.has(key) )
                    {
                        const copiedCell:MeshCopiedCell = copiedRangeCells.get(key);
                        const value:IValue = copiedCell.value;
                        let formulaToPaste:SlotFormula = copiedCell.formula;

                        if( Type.isInstanceOf(value, Type.Frame) )
                            formulaToPaste = value;
                        else
                            formulaToPaste = value && value.toString();

                        if( formulaToPaste )
                        {
                            sheetSettings.setCellFormat(destSheetId, new MeshRange(destLocation, destLocation),
                                MeshSheetSize.singleCell(), copiedCell.cellFormatId);
                            sheet.setSlotFormula(destSheetId, destLocation.toSlotLocation(), formulaToPaste, false);
                        }
                        else
                        {
                            mesh.clearCell(ctx, destColIndex, destRowIndex);
                        }
                    }
                    else
                    {
                        mesh.clearCell(ctx, destColIndex, destRowIndex);
                    }
                }
                else
                {
                    if( copiedRangeCells.has(key) )
                    {
                        const copiedCell:MeshCopiedCell = copiedRangeCells.get(key);
                        let formulaToPaste:SlotFormula = copiedCell.formula || copiedCell.value;

                        if( typeof formulaToPaste === 'string' )
                        {
                            const plainTokens = sheet.getPlainFormulaTokens(
                                formulaToPaste,
                                new SlotLocation(
                                    copiedRange.from.colIndex + colOffset,
                                    copiedRange.from.rowIndex + rowOffset
                                )
                            );
                            let finalPlainTokens:ILinearFormulaToken[] | null = plainTokens;

                            if( needRecalculateCellRefs )
                            {
                                finalPlainTokens = fic.recalculateCellReferencesForPastingCells(
                                    plainTokens,
                                    srcSheetId,
                                    copiedCell.location,
                                    destSheetId,
                                    copiedRange,
                                    toColIndex,
                                    toRowIndex
                                );
                            }

                            // for Copy operation we should check whether it is an !REF# error when paste
                            finalPlainTokens = fic.normalizeCellReferences(finalPlainTokens, destColIndex, destRowIndex);
                            formulaToPaste = fic.getFormulaTokensText(finalPlainTokens, destColIndex, destRowIndex, false);
                        }

                        if( formulaToPaste )
                        {
                            sheetSettings.setCellFormat(destSheetId, new MeshRange(destLocation, destLocation),
                                MeshSheetSize.singleCell(), copiedCell.cellFormatId);
                            sheet.setSlotFormula(destSheetId, destLocation.toSlotLocation(), formulaToPaste, false);
                        }
                        else
                        {
                            mesh.clearCell(ctx, destColIndex, destRowIndex);
                        }

                        if( copiedCell.isLazySlot )
                        {
                            sheet.setSlotValue(destSheetId, destLocation.toSlotLocation(), copiedCell.value);
                        }

                        // commonControllers.spreadsheets.sheetSettings.removeSheetSettingsForSlot(destSheetId, destLocation);

                        sheetSettings.removeCellFormat(destSheetId, new MeshRange(destLocation, destLocation));
                        sheetSettings.removeSheetSettingsForCell(destSheetId, destLocation);

                        copiedCell.sheetSettings.forEach(copiedSheetSettings =>
                        {
                            if( copiedSheetSettings.cellSettings.length > 0 )
                            {
                                if( copiedSheetSettings.slotId == null )
                                {
                                    // copy settings for top sheet
                                    copiedSheetSettings.cellSettings.forEach((cellSettings) =>
                                    {
                                        const newCellSettings = MeshCellSettings.fromJsonDeep(cellSettings);
                                        newCellSettings.id = IdUtil.newGUID();

                                        sheetSettings.setCellSettings(destSheetId, destLocation.asKey(), newCellSettings);
                                    });
                                }
                                else
                                {
                                    // copy settings for nested sheets

                                    // TODO: Fix after merge
                                    // const destSlot = slotData.getOrCreateSlot(destSheetId, destColIndex, destRowIndex);
                                    //
                                    // const sheetSettingsId = sheetSettings.changeSheetSettingsId(copiedSheetSettings.id, copiedSheetSettings.slotId, destSlot.id);
                                    // const destSheetSettings = sheetSettings.getOrCreateSheetSettings(destSheetId, sheetSettingsId, destSlot.id);
                                    //
                                    // if (destSheetSettings)
                                    // {
                                    //     copiedSheetSettings.cellSettings.forEach((cellSettings) => {
                                    //         sheetSettings.setCellSettings(destSheetId, destLocation.asKey(), MeshCellSettingsMST.create(cellSettings as any));
                                    //     });
                                    // }

                                    copiedSheetSettings.cellSettings.forEach((cellSettings, cellSettingsLocation) =>
                                    {
                                        // TODO: Fix after merge
                                        // sheetSettings.setCellSettings(destSheetId, cellSettingsLocation, MeshCellSettingsMST.create(cellSettings as any));
                                    });
                                }
                            }
                            if( copiedSheetSettings.cellNotes.length > 0 )
                            {
                                if( copiedSheetSettings.slotId == null )
                                {
                                    // copy settings for top sheet
                                    copiedSheetSettings.cellNotes.forEach((cellNote) =>
                                    {
                                        const newCellNote = MeshCellNote.fromJsonDeep(cellNote);
                                        newCellNote.id = IdUtil.newGUID();

                                        sheetSettings.setCellNote(destSheetId, destLocation.asKey(), newCellNote);
                                    });
                                }
                                else
                                {
                                    // copy settings for nested sheets

                                    // TODO: Fix after merge
                                    // const destSlot = slotData.getOrCreateSlot(destSheetId, destColIndex, destRowIndex);
                                    //
                                    // const sheetSettingsId = sheetSettings.changeSheetSettingsId(copiedSheetSettings.id, copiedSheetSettings.slotId, destSlot.id);
                                    // const destSheetSettings = sheetSettings.getOrCreateSheetSettings(destSheetId, sheetSettingsId, destSlot.id);
                                    //
                                    // if (destSheetSettings)
                                    // {
                                    //     copiedSheetSettings.cellSettings.forEach((cellSettings) => {
                                    //         sheetSettings.setCellSettings(destSheetId, destLocation.asKey(), MeshCellSettingsMST.create(cellSettings as any));
                                    //     });
                                    // }

                                    copiedSheetSettings.cellNotes.forEach((cellNote) =>
                                    {
                                        // TODO: Fix after merge
                                        // sheetSettings.setCellNote(destSheetId, destLocation.asKey(), MeshCellNoteMST.create(cellNote as any));
                                    });
                                }
                            }

                            const format = copiedCell.cellFormatId;
                            sheetSettings.setCellFormat(destSheetId, new MeshRange(destLocation, destLocation), MeshSheetSize.singleCell(), format);
                        });
                    }
                    else
                    {
                        sheetSettings.removeSheetSettingsForCell(destSheetId, destLocation);
                        sheetSettings.removeCellFormat(destSheetId, new MeshRange(destLocation, destLocation));
                        mesh.clearCell(ctx, destColIndex, destRowIndex);
                    }
                }
            }
        }
    }

    private _getSlotsGlobalKeysWithRefsToCutCells(srcSheetId:SheetId,
                                                  destSheetId:SheetId,
                                                  startColIndex:number,
                                                  startRowIndex:number,
                                                  copiedRange:MeshRange):Set<SlotGlobalKey>
    {
        const main = controllers.ui.spreadsheets.main;

        let slotsWithRefsToCutCellsGlobalKeys:Set<SlotGlobalKey> = new Set<SlotGlobalKey>();

        const sheet:AnySheet = main.findAnySheetById(srcSheetId);

        if( !sheet )
            return slotsWithRefsToCutCellsGlobalKeys;

        const workbook:Workbook = main.findWorkbookById(sheet.workbook.id);
        const dependencies:Map<SlotGlobalKey, SlotDependencies> = workbook.runtime.slotDependencies;
        const lazySlotDependencies:Map<SlotGlobalKey, Set<SlotGlobalKey>> = workbook.runtime.lazySlotDependencies;

        const getSlotGlobalKeysForRange = (sheetId:SheetId, range:MeshRange):Set<SlotGlobalKey> =>
        {
            let rangeSlotsGlobalKeys:Set<SlotGlobalKey> = new Set<SlotGlobalKey>();

            for( let col:number = range.from.colIndex; col <= range.to.colIndex; col++ )
            {
                for( let row:number = range.from.rowIndex; row <= range.to.rowIndex; row++ )
                {
                    rangeSlotsGlobalKeys.add(new SlotLocation(col, row).toGlobalKey(sheetId));
                }
            }

            return rangeSlotsGlobalKeys;
        };

        const haveIntersection = (set1:Set<any>, set2:Set<any>):boolean =>
        {
            for( let value of Array.from(set1.values()) )
            {
                if( set2.has(value) )
                    return true;
            }

            return false;
        };

        const copiedRangeSlotsGlobalKeys:Set<SlotGlobalKey> = getSlotGlobalKeysForRange(srcSheetId, copiedRange);

        const destRangeSlotsGlobalKeys:Set<SlotGlobalKey> = getSlotGlobalKeysForRange(
            destSheetId,
            new MeshRange(
                new MeshLocation(startColIndex, startRowIndex),
                new MeshLocation(startColIndex + copiedRange.columnsCount - 1, startRowIndex + copiedRange.rowsCount - 1)
            )
        );

        dependencies.forEach((mapOfDependencies:SlotDependencies, slotWithExternalRefKey:SlotGlobalKey) =>
        {
            if(
                haveIntersection(new Set(mapOfDependencies.keys()), copiedRangeSlotsGlobalKeys) &&
                !destRangeSlotsGlobalKeys.has(slotWithExternalRefKey) &&
                !copiedRangeSlotsGlobalKeys.has(slotWithExternalRefKey)
            )
                slotsWithRefsToCutCellsGlobalKeys.add(slotWithExternalRefKey);
        });

        lazySlotDependencies.forEach((mapOfDependencies:Set<SlotGlobalKey>, slotWithExternalRefKey:SlotGlobalKey) =>
        {
            if(
                haveIntersection(mapOfDependencies, copiedRangeSlotsGlobalKeys) &&
                !destRangeSlotsGlobalKeys.has(slotWithExternalRefKey) &&
                !copiedRangeSlotsGlobalKeys.has(slotWithExternalRefKey)
            )
                slotsWithRefsToCutCellsGlobalKeys.add(slotWithExternalRefKey);
        });

        return slotsWithRefsToCutCellsGlobalKeys;
    }

    private _recalculateCellRefsForSlotsWithRefsToCutCells(srcSheetId:SheetId,
                                                           destSheetId:SheetId,
                                                           startColIndex:number,
                                                           startRowIndex:number,
                                                           copiedRange:MeshRange,
                                                           slotsGlobalKeysWithRefsToCutCells:Set<SlotGlobalKey>):void
    {
        const {formulaInput: fic, sheet} = controllers.ui.spreadsheets;
        const ctx:IWSPMeshContext = this._getMeshContextBySheetId(srcSheetId);

        slotsGlobalKeysWithRefsToCutCells.forEach((slotGlobalKey:SlotGlobalKey):void =>
        {
            const {sheetId, columnIndex, rowIndex}:ISheetLocation = SlotLocation.fromGlobalKey(slotGlobalKey);
            const slotLocation:SlotLocation = new SlotLocation(columnIndex, rowIndex);

            const slot = sheet.getOrCreateSlot(sheetId, slotLocation);
            const plainTokens = sheet.getPlainFormulaTokens(slot.formula, slot.location);

            // need to select cellRef and cellRefRange tokens which have refs to cut slots and recalculate only them

            let finalPlainTokens:ILinearFormulaToken[] = fic.recalculateCellReferencesForSlotsWithRefsToCutCells(
                plainTokens,
                sheetId,
                slot.location,
                srcSheetId,
                destSheetId,
                copiedRange,
                startColIndex,
                startRowIndex
            );

            finalPlainTokens = fic.normalizeCellReferences(finalPlainTokens, columnIndex, rowIndex);
            const formulaToPaste:SlotFormula = fic.getFormulaTokensText(finalPlainTokens, columnIndex, rowIndex, false);

            sheet.setSlotFormula(sheetId, slotLocation, formulaToPaste, false);
        });
    }

    private _relocateNamedRanges(srcSheetId:SheetId,
                                 destSheetId:SheetId,
                                 pasteColIndex:number,
                                 pasteRowIndex:number,
                                 copiedRangeCells:Map<CopiedCellOffsetsKey, MeshCopiedCell>):void
    {

        const {sheet, namedRanges} = controllers.ui.spreadsheets;

        for( const copiedCell of Array.from(copiedRangeCells.values()) )
        {
            const {rowOffset, colOffset, namedRangeIds} = copiedCell;
            const newLocation = new SlotLocation(pasteColIndex + colOffset, pasteRowIndex + rowOffset);

            namedRangeIds
                .map(rangeId => namedRanges.getNamedRangeById(rangeId))
                .forEach(range =>
                {
                    range.sheetId = destSheetId;
                    range.location = newLocation;
                });
        }
    }

    private _clearCutCells(srcSheetId:SheetId,
                           destSheetId:SheetId,
                           fromColIndex:number,
                           fromRowIndex:number,
                           copiedRangeCells:Map<CopiedCellOffsetsKey, MeshCopiedCell>):void
    {
        const {mesh, sheetSettings} = controllers.ui.spreadsheets;
        const ctx:IWSPMeshContext = this._getMeshContextBySheetId(srcSheetId);

        const locationsToClear:Map<MeshLocationKey, MeshLocation> = new Map();
        copiedRangeCells.forEach((cell) =>
        {
            const srcLocation = MeshLocation.fromSlotLocation(cell.location);
            locationsToClear.set(srcLocation.asKey(), srcLocation);
        });

        //don't clear cells that we will paste in
        if( srcSheetId === destSheetId )
        {
            copiedRangeCells.forEach((cell) =>
            {
                const destColIndex = fromColIndex + cell.colOffset;
                const destRowIndex = fromRowIndex + cell.rowOffset;
                const destLocation = new MeshLocation(destColIndex, destRowIndex);
                locationsToClear.delete(destLocation.asKey());
            });
        }

        locationsToClear.forEach((location) =>
        {
            sheetSettings.setCellSettings(srcSheetId, location.asKey(), null);
            sheetSettings.setCellNote(srcSheetId, location.asKey(), null);
            sheetSettings.removeCellFormat(srcSheetId, new MeshRange(location, location));

            mesh.clearCell(ctx, location.colIndex, location.rowIndex);
        });
    }

    private _getMeshContextBySheetId(sheetId:SheetId, isNestedSheet:boolean = false):IWSPMeshContext
    {
        const {workbook} = controllers.ui.spreadsheets;

        return workbook.getMeshContextBySheetId(sheetId);
    }
}
